// components/secure/pm/BusinessScreening.js — the rest of a commercial or
// industrial application after the underwriting: the decision, the letter, the
// FCRA notice where a person's report was used, and the documents on file.
//
// The decision is documented the same way every time, because consistency is the
// defence on a business tenancy just as uniform criteria are on a residential one:
//
//   * a decline names its reasons — picked from the underwriting results the
//     server computed, so the words on the letter are the words on the file;
//   * a conditional approval lists its conditions — the recommended guaranty and
//     any open environmental items are offered as ready-made conditions;
//   * approving over a shortfall needs a written reason.
//
// The server enforces all three (lib/screening.checkBusinessDecision); the screen
// only makes the right answer the easy one.
import React, { useEffect, useState, useCallback } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { Pill, Input, Select, Textarea, Button, Notice, fmtDate, titleCase } from './PMKit';
import { GUARANTY_LABEL } from './BusinessUnderwriting';

const OUTCOMES = [
    { value: '', label: 'Select…' },
    { value: 'approved', label: 'Approve' },
    { value: 'approved_with_conditions', label: 'Approve with conditions' },
    { value: 'declined', label: 'Decline' },
    { value: 'withdrawn', label: 'Withdrawn by applicant' },
];

const Box = ({ title, children }) => (
    <div className="mt-4 border-t border-slate-100 pt-3">
        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
        {children}
    </div>
);

// Pick-list plus free text. Returns the chosen strings.
const PickList = ({ label, suggestions, value, onChange }) => {
    const [extra, setExtra] = useState('');
    const toggle = (s) => onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s]);
    const custom = value.filter((v) => !suggestions.includes(v));
    return (
        <div className="mb-3">
            <p className="text-xs font-medium text-slate-600">{label}</p>
            <div className="mt-1 space-y-1 text-sm text-slate-700">
                {suggestions.map((s) => (
                    <label key={s} className="flex items-start gap-2">
                        <input type="checkbox" className="mt-1" checked={value.includes(s)} onChange={() => toggle(s)} />
                        <span>{s}</span>
                    </label>
                ))}
                {custom.map((s) => (
                    <label key={s} className="flex items-start gap-2">
                        <input type="checkbox" className="mt-1" checked onChange={() => toggle(s)} />
                        <span>{s}</span>
                    </label>
                ))}
            </div>
            <div className="mt-1 flex gap-2">
                <input className="flex-1 rounded border border-slate-300 p-2 text-sm" placeholder="Add your own…"
                       value={extra} onChange={(e) => setExtra(e.target.value)} />
                <Button variant="ghost" type="button" disabled={!extra.trim()}
                        onClick={() => { onChange([...value, extra.trim()]); setExtra(''); }}>Add</Button>
            </div>
        </div>
    );
};

// Fetch a protected file and open it. apiClient.get only speaks JSON.
const openProtected = async (url) => {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
    if (!r.ok) {
        let m = `HTTP ${r.status}`;
        try { m = (await r.json()).message || m; } catch { /* not JSON */ }
        throw new Error(m);
    }
    window.open(URL.createObjectURL(await r.blob()), '_blank', 'noopener');
};

// ------------------------------------------------------------------ decision
export const BusinessDecision = ({ applicationId, application, screening, business, assessed, onDone }) => {
    const apiClient = useApiClient();
    const pulled = !!Number(business?.principal_credit_pulled)
        || (business?.guarantors || []).some((g) => g && g.credit_pulled);
    const [f, setF] = useState({ outcome: '', reasons: [], conditions: [], note: '',
                                 consumer_report_used: pulled, reporting_agency: application?.reporting_agency || '' });
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    const failures = assessed?.underwriting?.failures || [];
    const open = assessed?.environmental?.open || [];
    const recommended = assessed?.underwriting?.guarantyRecommended;
    const conditionSuggestions = [
        ...(recommended && recommended !== 'none' && recommended !== 'case_by_case'
            ? [`${GUARANTY_LABEL[recommended]} delivered before the lease is signed`] : []),
        ...open.map((o) => `Before possession: ${o}`),
    ];
    const reportNeeded = f.outcome === 'declined' && f.consumer_report_used;

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            await apiClient.post(`/api/screening/applications/${applicationId}/decide`, {
                outcome: f.outcome, decision_reasons: f.reasons, decision_conditions: f.conditions,
                outcome_reason: f.note, consumer_report_used: !!f.consumer_report_used,
                reporting_agency: f.reporting_agency || null,
            });
            onDone?.();
        } catch (e) { setErr(e); } finally { setBusy(false); }
    };

    if (!business) {
        return <Box title="Decision"><p className="text-sm text-slate-500">Run the underwriting first; the decision is made on it.</p></Box>;
    }

    return (
        <Box title="Decision">
            {err && <Notice tone="bad" title="Not recorded" message={err.message} problems={err.problems} />}
            <div className="grid gap-x-4 sm:grid-cols-2">
                <Select label="Outcome" value={f.outcome} options={OUTCOMES}
                        onChange={(e) => setF({ ...f, outcome: e.target.value })} />
            </div>
            {f.outcome === 'declined' && (
                <PickList label="Reasons (these are printed on the letter)" suggestions={failures}
                          value={f.reasons} onChange={(reasons) => setF({ ...f, reasons })} />
            )}
            {f.outcome === 'approved_with_conditions' && (
                <PickList label="Conditions the tenant must meet" suggestions={conditionSuggestions}
                          value={f.conditions} onChange={(conditions) => setF({ ...f, conditions })} />
            )}
            {f.outcome === 'approved' && failures.length > 0 && (
                <p className="mb-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Underwriting fell short of the published criteria. Approving outright needs a written reason below —
                    or approve with conditions instead.
                </p>
            )}
            {f.outcome && (
                <Textarea label={f.outcome === 'approved' && failures.length ? 'Reason for approving over the shortfall (required)' : 'Note for the file'}
                          rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            )}
            {f.outcome && (
                <div className="mb-2 flex flex-wrap gap-4 text-sm text-slate-600">
                    <label className="flex items-center gap-1">
                        <input type="checkbox" checked={!!f.consumer_report_used}
                               onChange={(e) => setF({ ...f, consumer_report_used: e.target.checked })} />
                        A personal consumer report (owner or guarantor) was relied on
                    </label>
                </div>
            )}
            {pulled && !f.consumer_report_used && f.outcome === 'declined' && (
                <p className="mb-2 text-xs text-amber-800">
                    A personal report was pulled on this file. Untick only if the decline genuinely did not rest on it.
                </p>
            )}
            {reportNeeded && (
                <Input label="Consumer reporting agency that supplied the report (required)" value={f.reporting_agency}
                       onChange={(e) => setF({ ...f, reporting_agency: e.target.value })}
                       hint="FCRA § 615(a): the notice must name it, so the person can get a free copy and dispute it." />
            )}
            <Button disabled={busy || !f.outcome} onClick={submit}>Record decision</Button>
        </Box>
    );
};

// ------------------------------------------------------------------ decided: letter + FCRA
export const BusinessDecided = ({ applicationId, application, screening, business, onDone }) => {
    const apiClient = useApiClient();
    const fcra = Number(application?.adverse_action_required) === 1;
    const [agency, setAgency] = useState({ agency_name: application?.reporting_agency || '', agency_address: '',
                                           agency_phone: '', report_subject: business?.principal_name
                                               || (business?.guarantors || []).find((g) => g.credit_pulled)?.name || '' });
    const [sent, setSent] = useState({ sent_on: new Date().toISOString().slice(0, 10), method: 'mail' });
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);
    const q = new URLSearchParams(Object.fromEntries(Object.entries(agency).filter(([, v]) => v))).toString();

    const preview = async () => {
        setMsg(null);
        try {
            const r = await apiClient.get(`/api/screening/applications/${applicationId}/decision-letter?${q}`);
            window.open(URL.createObjectURL(new Blob([r.html], { type: 'text/html' })), '_blank', 'noopener');
            if (r.missing?.length) setMsg({ tone: 'warn', text: `Still blank on the letter: ${r.missing.join(', ')}.` });
        } catch (e) { setMsg({ tone: 'bad', text: e.message }); }
    };
    const file = async () => {
        setBusy(true); setMsg(null);
        try {
            const r = await apiClient.post(`/api/screening/applications/${applicationId}/decision-letter`, agency);
            setMsg({ tone: 'good', text: r.message }); onDone?.();
        } catch (e) { setMsg({ tone: 'bad', text: e.message }); } finally { setBusy(false); }
    };
    const recordSent = async () => {
        setBusy(true); setMsg(null);
        try {
            await apiClient.post(`/api/screening/applications/${applicationId}/adverse-action`,
                { ...sent, reporting_agency: agency.agency_name || null });
            setMsg({ tone: 'good', text: 'Adverse action notice recorded as sent.' }); onDone?.();
        } catch (e) { setMsg({ tone: 'bad', text: e.message }); } finally { setBusy(false); }
    };

    return (
        <Box title="Decision">
            <div className="flex flex-wrap items-center gap-2">
                <Pill tone={screening.outcome === 'declined' ? 'bad' : screening.outcome === 'withdrawn' ? 'slate' : 'good'}>
                    {titleCase(screening.outcome)}
                </Pill>
                <span className="text-xs text-slate-400">{fmtDate(screening.decided_on)} · criteria {application?.criteria_version}</span>
            </div>
            {(business?.decision_reasons || []).length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                    {business.decision_reasons.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
            )}
            {(business?.decision_conditions || []).length > 0 && (
                <>
                    <p className="mt-2 text-xs font-semibold text-slate-500">Conditions</p>
                    <ul className="list-disc pl-5 text-sm text-slate-700">
                        {business.decision_conditions.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                </>
            )}
            {screening.outcome_reason && !(business?.decision_reasons || []).length && !(business?.decision_conditions || []).length && (
                <p className="mt-1 text-sm text-slate-600">{screening.outcome_reason}</p>
            )}

            {msg && <div className="mt-3"><Notice tone={msg.tone} message={msg.text} onDismiss={() => setMsg(null)} /></div>}

            <div className="mt-3">
                <p className="text-xs font-medium text-slate-600">Decision letter</p>
                {fcra && (
                    <div className="mt-1 grid gap-x-4 sm:grid-cols-2">
                        <Input label="Reporting agency" value={agency.agency_name}
                               onChange={(e) => setAgency({ ...agency, agency_name: e.target.value })} />
                        <Input label="Agency telephone" value={agency.agency_phone}
                               onChange={(e) => setAgency({ ...agency, agency_phone: e.target.value })} />
                        <Input label="Agency address" className="sm:col-span-2" value={agency.agency_address}
                               onChange={(e) => setAgency({ ...agency, agency_address: e.target.value })} />
                        <Input label="Addressed to (the person whose report was used)" value={agency.report_subject}
                               onChange={(e) => setAgency({ ...agency, report_subject: e.target.value })} />
                    </div>
                )}
                <div className="mt-1 flex flex-wrap gap-2">
                    <Button variant="ghost" onClick={preview}>Preview / print letter</Button>
                    <Button disabled={busy} onClick={file}>File letter on the application</Button>
                </div>
                {business?.decision_letter_document_id && (
                    <p className="mt-1 text-xs text-slate-500">A letter is on file (see Documents).</p>
                )}
            </div>

            {fcra && (
                application.adverse_action_sent_on ? (
                    <p className="mt-3 text-sm text-emerald-800">
                        FCRA adverse action notice sent {fmtDate(application.adverse_action_sent_on)}
                        {application.adverse_action_method ? ` by ${titleCase(application.adverse_action_method)}` : ''}.
                    </p>
                ) : (
                    <div className="mt-3">
                        <Notice tone="bad" title="FCRA adverse action notice is owed"
                            message="A consumer report on an individual was relied on. Send the letter above (it carries the notice), then record when and how it went." />
                        <div className="grid gap-x-4 sm:grid-cols-3">
                            <Input label="Sent on" type="date" value={sent.sent_on}
                                   onChange={(e) => setSent({ ...sent, sent_on: e.target.value })} />
                            <Select label="Method" value={sent.method} options={['mail', 'email', 'in_person']}
                                    onChange={(e) => setSent({ ...sent, method: e.target.value })} />
                        </div>
                        <Button variant="ghost" disabled={busy} onClick={recordSent}>Record notice sent</Button>
                    </div>
                )
            )}
        </Box>
    );
};

// ------------------------------------------------------------------ documents
const DOC_TYPES = [
    ['financial_statements', 'Financial statements'], ['tax_returns', 'Tax returns'],
    ['bank_statements', 'Bank statements'], ['bank_reference', 'Bank reference letter'],
    ['trade_reference', 'Trade reference'], ['business_credit_report', 'Business credit report'],
    ['personal_credit_report', 'Personal credit report (consumer report)'], ['guaranty', 'Guaranty'],
    ['personal_financial_statement', 'Personal financial statement'], ['formation_documents', 'Formation documents'],
    ['good_standing', 'Certificate of good standing'], ['insurance_certificate', 'Insurance certificate'],
    ['phase_i_esa', 'Phase I ESA'], ['environmental_permit', 'Environmental permit'],
    ['business_plan', 'Business plan'], ['other', 'Other'],
];
const DOC_LABEL = Object.fromEntries([...DOC_TYPES, ['decision_letter', 'Decision letter'],
    ['adverse_action_notice', 'Decision letter with FCRA notice']]);

export const ApplicationDocuments = ({ applicationId, documents: initial, readOnly }) => {
    const apiClient = useApiClient();
    const [docs, setDocs] = useState(initial || []);
    const [docType, setDocType] = useState('financial_statements');
    const [title, setTitle] = useState('');
    const [fileObj, setFileObj] = useState(null);
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);
    const [inputKey, setInputKey] = useState(0);

    const load = useCallback(async () => {
        try { setDocs(await apiClient.get(`/api/screening/applications/${applicationId}/documents`)); }
        catch (e) { setMsg({ tone: 'bad', text: e.message }); }
    }, [apiClient, applicationId]);
    useEffect(() => { setDocs(initial || []); }, [initial]);

    const upload = async () => {
        if (!fileObj) return;
        setBusy(true); setMsg(null);
        try {
            const fd = new FormData();
            fd.append('doc_type', docType);
            fd.append('title', title || fileObj.name);
            fd.append('file', fileObj);
            await apiClient.post(`/api/screening/applications/${applicationId}/documents`, fd, true);
            setTitle(''); setFileObj(null); setInputKey((k) => k + 1);
            setMsg({ tone: 'good', text: 'Filed.' });
            await load();
        } catch (e) { setMsg({ tone: 'bad', text: e.message }); } finally { setBusy(false); }
    };

    return (
        <Box title="Documents on file">
            {msg && <Notice tone={msg.tone} message={msg.text} onDismiss={() => setMsg(null)} />}
            {docs.length === 0 ? (
                <p className="text-sm text-slate-500">Nothing filed yet. Statements, references, certificates and the decision letter belong here.</p>
            ) : (
                <ul className="divide-y divide-slate-100 text-sm">
                    {docs.map((d) => (
                        <li key={d.pm_document_id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                            <span>
                                <span className="text-slate-800">{d.title}</span>{' '}
                                <span className="text-xs text-slate-400">{DOC_LABEL[d.doc_type] || titleCase(d.doc_type)} · {fmtDate(d.uploaded_at)}</span>
                            </span>
                            <button type="button" className="text-xs text-indigo-600 hover:underline"
                                onClick={() => openProtected(`/api/screening/applications/${applicationId}/documents/${d.pm_document_id}/download`)
                                    .catch((e) => setMsg({ tone: 'bad', text: e.message }))}>Open</button>
                        </li>
                    ))}
                </ul>
            )}
            {!readOnly && (
                <div className="mt-3 grid items-end gap-x-3 sm:grid-cols-4">
                    <Select label="Kind" value={docType} onChange={(e) => setDocType(e.target.value)}
                            options={DOC_TYPES.map(([value, label]) => ({ value, label }))} />
                    <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 2025 audited statements" />
                    <label className="mb-3 block text-xs font-medium text-slate-600">File (25 MB max)
                        <input key={inputKey} type="file" className="mt-1 block w-full text-sm"
                               onChange={(e) => setFileObj(e.target.files?.[0] || null)} />
                    </label>
                    <div className="mb-3"><Button disabled={busy || !fileObj} onClick={upload}>Upload</Button></div>
                </div>
            )}
            <p className="text-xs text-slate-400">Staff only. Nothing here is shown on the owner or tenant portal.</p>
        </Box>
    );
};
